import { ChartData, ChartOptions, ChartType } from 'chart.js';
import { AccountGoal } from '../../shared/models/models';
import { UnitType } from '../experience/models';
import { StreamContent } from '../experience/streams-models';
import { AceCampaignType } from '../experience/ace-models';

export interface KeyValue {
  id: string;
  name: string;
}

export interface TrenDemonAcceleratedStats {
  goals_reached_accelerated: number;
  journeys_accelerated: number;
  sf_pipelift?: number;
  goals_reached_not_accelerated: number;
}

export interface TrdUpliftStats {
  journeys: number;
  coverage: number;
  engagement_uplift?: number;
  conv_rate_uplift?: number;
  goals_reached_accelerated?: number;
  journeys_accelerated?: number;
  engaged_avg_reads: number;
  not_engaged_avg_reads: number;
  engaged_converted: number;
  not_engaged_converted: number;
  journeys_not_accelerated: number;
  goals_reached_not_accelerated: number;
  deals_amount: number;
  impacted_deals_amount: number;
  deals_count: number;
  impacted_deals_count: number;
  not_engaged_deals_rate: number;
  engaged_deals_rate: number;
  deals_rate_uplift: number;
}

export interface DashboardGoalStatsView extends AccountGoal {
  count: number;
  contentInfluancePercent: number;
  changePercent: number;
  period: number;
}

export interface DashboardGoalStats {
  goal_id: number;
  total_reached: string; // numeric string (integer)
  content_reached: string; // numeric string (integer)
  ratio: string; // double with two digits precision
  percentage_from_all: string; // numeric string (integer)
}


export interface OpportunitiesAudienceStats {
  opportunitiesCount: number;
  prevOpportunitiesCount?: number;
  opportunitiesValue: number;
  prevOpportunitiesValue?: number;
  assistedOpportunitiesValue?: number;
  prevAssistedOpportunitiesValue?: number;
}
export interface DashboardGoalReachesCount {
  ts_date: string; // date in format yyyy-mm-dd
  total: number;
  content: number;
  no_content: number;
}

export interface DashboardTableItem {
  media: string;
  title: string;
  visits: number;
  completed: number;
  completionRatio: number;
  readRatio: number;
  proceedRatio: number;
  landing: number;
  nurture: number;
  conversion: number;
  attributionScore: number;

}

export type SourceType = 'source' | 'campaign';
export type SourcesExportType = 'source_list' | 'first_touch';

export interface SourcesTableItem extends SourcesResponseItemBase {
  name: string;
  type: string;
  typeColor: string;
}

export interface SourcesResponseItemSourceType extends SourcesResponseItemBase {
  name: string;
  type: number;
}

export interface SourcesResponseItemCampaignType extends SourcesResponseItemBase {
  utm_source: string;
  utm_campaign: string;
  type: number;
}

interface SourcesResponseItemBase {
  visitors: number;
  sessions: number;
  readRatio: number;
  conversions: number;
  organicUplift: number;
  landing: number;
  nurture: number;
  conversion: number;
  attribution: number;
}

export interface SourcesTotalsResponse {
  count: number;
  goalsReached: number;
  distribution: SourcesDistributionItem[] | CampaignsDistributionItem[];
}

export interface SourcesChartItem {
  name: string;
  y: number;
  percent?: number;
  color: string;
}

export interface SourcesDistributionItem {
  type: number;
  conversions: number;
  conversionsRatio: number;
}

export interface CampaignsDistributionItem {
  utm_source: string;
  conversions: number;
  conversionsRatio: number;
}

export interface InsightsAreasData {
  areaId: number;
  by: string;
  contentType: string;
  pattern: string;
  name: string;
}

export interface InsightsTotals {
  conversions: number;
  conversionsPercent?: number;
  pages?: number;
  participatingPages?: number;
  participatingPagesPercent?: number;
  readRatio?: number;
  readRatioPercent?: number;
  revenue?: number;
  revenuePercent?: number;
  conversionInfluencedByContentRatio?: number;
  influencedByContentDiffPercents?: number;
}

export interface PagesCount {
  pages: number;
}

export interface AgregatedContentData {
  readRatio?: number;
  conversions: number;
  revenue?: number;
  conversionInfluencedByContentRatio?: number;
  visits?: number;
  conversionRatio?: number;
  companies?: number;
  deals?: number;
  deals_value?: number;
}


export interface InsightsContentCompareGraph {
  ts_date: string;
  fromDate: string;
  toDate: string;
  visits: number;
  conversions: number;
  conversionRatio: number;
}

export interface InsightsContentTableItem {
  url_id: number;
  url: string;
  page_id: number;
  page_uid: number;
  areaid: number;
  area_name: string;
  page_views: number;
  visitors: number;
  page_reads: number;
  read_ratio: number;
  conversions: number;
  reach_ratio: number;
  proceed_ratio: number;
  img_link: string;
  title: string;
  attribution: number;
  landing: number;
  nurture: number;
  conversion: number;
  boost_score: number;
  value_per_visit?: number;
  deals_value?: number;
  deals?: number;
  companies?: number;
  deals_rate?: number;
}

export interface InsightsJourneysTotals {
  conversions: number;
  pages: number;
  sessions: number;
  time_length: number;
  can_delete: boolean;
}

export interface AbmJourneysTotals {
  visitors: number;
  companies: number;
  read_rate: number;
  goals_reached_precentage: number;
  clean_companies: number;
}

export interface VisitorsJourneysTotals {
  journeys: number;
  pages: number;
  sessions: number;
  goals_reached_precentage: number;
  can_delete: boolean;
  read_rate: number;
}

export interface InsightsJourneysTableItem {
  country_code: string;
  ipdata: string;
  country_name: string;
  domain: string;
  duration: string;
  email: string;
  company_name: string;
  company_size: string;
  event_date: number;
  pagenum: number;
  session: number;
  start_thread: number;
  tbegin: number;
  user_ip: string;
  utm_campaign: string;
  utm_source: string;
  sessions_details: string;
  milestones: string;
  actions_list: string;
  last_goal_id: number;
  details?: JourneysDetails;
  loadingDetails?: boolean;
  visitor_uid?: string;
  goals_count?: number;
  passed_via_aichat: 0 | 1;
}

export interface InsightsJourneysDetailsRequest {
  start_time: string;
  end_time: string;
  sessions_details: string;
  milestones: string;
  last_goal_id: string;
}


export interface CampaignsAttributes {
  referrals: string[];
  utm_sources: string[];
  utm_campaigns: string[];
}

export interface JourneysDetails {
  breakdown_sessions: BreakDownSession[];
  journey_overview: JourneyOverview;
}

export interface IAiChatConversation {
  firstResponseId: string;
  pageUid: string;
  messages: IVisitorAiChatMessage[];
}

export interface IVisitorAiChatMessage {
  prompt: string;
  response: string;
}

export interface BreakDownSession {
  circles: BreakDownSessionFirstCircles[];
  fbox: BreakDownSessionFirstBlock;
}

export interface BreakDownSessionFirstBlock {
  date: string;
  domain: string;
  user: string;
}
export interface BreakDownSessionFirstCircles {
  img: string;
  imgdef: number;
  trd_assisted: number;
  txt: string;
  url: string;
  unit_type: string;
  read: boolean;
  variants: string[];
  otw_campaign: string;
  stream: string;
  first_ai_resp: string;
}

export interface JourneyOverview {
  startitem: JourneyOverviewInfo;
  circles: JourneyOverviewCircle[];
}

export interface JourneyOverviewInfo {
  date: string;
  name: string;
  system: 'Desktop' | 'Mobile';
}

export interface JourneyOverviewCircle {
  duration: number;
  duration_day: string;
  event_date: string;
  goalname: string;
  imgdef: 0 | 1 | 2 | 3;
  imgsrc: string;
  pages: number;
  session_count: number;
  session_source: string;
  formatted?: any;
}

export interface InsightsUnitStats {
  impressions: number;
  clicks: number;
  ctr: number;
}

export interface InsightsUnitGraphItem {
  ts_date: string;
  fromDate: string;
  toDate: string;
  impressions: number;
  clicks: number;
  ctr: number;
}

export interface InsightsCtaPerformance {
  id: string;
  unitType: UnitType;
  type: string;
  status: string;
  order: string;
  name: string;
  behavior: string;
  impressions: number;
  clicks: number;
  clickedLinks?: number | null;
  ctaBtnClicks?: number | null;
  ctaBtnImpressions?: number | null;
  ctaBtnCtr?: number | null;
  conversions: number;
  layout: string;
  createDate: string;
  companies?: number;
  deals?: number;
  deals_value?: number;
}
export interface InsightsPersonalizationTableItem extends InsightsCtaPerformance {
  ctr: number;
  conversionRate: number;
  editLink: string;
  ctaBtnClicks?: number | null;
  ctaBtnImpressions?: number | null;
  ctaBtnCtr?: number | null;
}
export interface InsightsPersonalizationClicksImpressions {
  id: number;
  totalExpress: number;
  totalClicks: number;
}
export interface InsightsPersonalizationGoals {
  id: number;
  ctagoals: number;
}

export interface IChart2<TType extends ChartType> {
  chartType: ChartType;
  data: ChartData<TType>;
  options: ChartOptions<TType> & ChartCustomOptions;
  legend: boolean;
}

export interface ChartCustomOptions {
  centerText?: { display: boolean; text: string };
}

export interface DateRange {
  begin: Date;
  end: Date;
}

export interface InsightsEventGoalsTableItem {
  reachedEventAndGoal: number;
  reachedEvent: number;
  reachedGoal: number;
  attribution: number;
  goalName: string;
  goalType: string;
  isGoal: number;
}
export interface InsightsEventGoalsTotals {
  conversions: number;
  eventsInfluence: number;
}

export enum SourceDomainType {
  Direct = 1,
  Organic = 2,
  Paid = 3,
  Email = 4,
  Social = 5,
  Referral = 6,
  LLM = 7
}

export const SOURCES_TYPES_MAP = {
  1: 'direct',
  2: 'organic search',
  3: 'paid',
  4: 'email',
  5: 'social',
  6: 'referral',
  7: 'LLM chat'
};


export interface Industrie {
  name: string;
}

export interface SurveyQuestionResult {
  question_id: number;
  question: string;
  answer_id: number;
  answer: string;
  count: number;
}

export interface SurveyQuestionAnswer {
  answer_id: number;
  answer: string;
  count: number;
}

export interface UnitAllTimeStats extends InsightsUnitStats {
  publish_date: string;
}


export interface InsightsAreasUnitsPerformance {
  area_id: number;
  area_name: string;
  impressions: number;
  clicks: number;
  ctr: number;
  page_views: number;
  page_reads: number;
  read_ratio: number;
  coverage: number;
}

export interface AceCampaignPerformance {
  campaign_type: AceCampaignType;
  name: string;
  variants: number;
  status: string;
  url: string;
  has_www: boolean;
  has_end_slash: boolean;
  page_views: number;
  visits: number;
  completed: number;
  read_ratio: number;
  completed_rate: number;
  proceed_ratio: number;
  is_ab_test: boolean;
  campaign_id: number;
  id: number;
  winner_variant_id: number | null;
  is_control_variant: boolean | null;
}

export interface AceCampaignPerformanceSort extends AceCampaignPerformance {
  sort?: number;
  sortLvl2?: number;
}

export interface OtwPerformance {
  name: string;
  url: string;
  target_list_id: number;
  target_list_name: string;
  contacts: number;
  contact_visits: number;
  contact_visitors: number;
  visits: number;
  completed: number;
  completed_rate: number;
  read_ratio: number;
  proceed_ratio: number;
  first_content: StreamContent;
  campaign_id: number;
  id: number;
  tags: [string];
  preview_short_url?: string;
  inb_impressions?: number;
  inb_clicks?: number;
  inb_ctr?: number;
  created_at: string;
}

export interface StreamPerformanceSort extends OtwPerformance {
  sort?: number;
  sortLvl2?: number;
  isContent?: boolean;
}
